/* Designer overview — sorting + "load more" reveal.
   All designer cards are rendered up front; cards past the first page start
   hidden (see novicell_designer-card.liquid). Sorting reorders the cards in the
   DOM and resets the window back to the first page. "Load more" reveals the
   next `per_page` batch. Scoped per section so multiple instances work. */

function initDesignerOverview(section) {
  // Re-initialising (theme editor) drops the previous run's listeners, including
  // the document-level one that closes the sort menu.
  if (section._designerAbort) section._designerAbort.abort();
  const controller = new AbortController();
  section._designerAbort = controller;
  const signal = controller.signal;

  const grid = section.querySelector('[data-designer-grid]');
  if (!grid) return;

  const button = section.querySelector('[data-designer-load-more]');
  const countEl = section.querySelector('[data-designer-count]');
  const cards = Array.from(grid.querySelectorAll('[data-designer-card]'));
  const total = cards.length;
  const perPage = button ? parseInt(button.dataset.perPage, 10) || total : total;
  let visible = button ? parseInt(button.dataset.visible, 10) || perPage : total;

  const countTemplate = countEl ? countEl.dataset.countText || '' : '';
  const allTemplate = countEl ? countEl.dataset.allText || '' : '';

  function updateCount() {
    if (!countEl) return;
    countEl.textContent =
      visible >= total
        ? allTemplate.replace('__Y__', total)
        : countTemplate.replace('__X__', visible).replace('__Y__', total);
  }

  // Show the first `visible` cards, hide the rest, and sync the button/count.
  function applyWindow() {
    cards.forEach((card, i) => {
      if (i < visible) card.removeAttribute('hidden');
      else card.setAttribute('hidden', '');
    });
    updateCount();
    if (button) button.toggleAttribute('hidden', visible >= total);
  }

  // Load more
  if (button) {
    button.addEventListener(
      'click',
      () => {
        visible = Math.min(visible + perPage, total);
        applyWindow();
      },
      { signal }
    );
  }

  // Sorting
  const sort = section.querySelector('[data-designer-sort]');
  if (!sort) return;

  const summary = sort.querySelector('summary');
  const options = Array.from(sort.querySelectorAll('[data-sort-value]'));

  // Collate in the storefront's language, not the visitor's browser default, so
  // Danish Æ/Ø/Å sort after Z rather than next to A/O. An empty `lang` is not a
  // valid language tag, so fall back to the runtime default.
  const collator = new Intl.Collator(document.documentElement.lang || undefined);

  const comparators = {
    'name-asc': (a, b) => collator.compare(a.dataset.sortName, b.dataset.sortName),
    'name-desc': (a, b) => collator.compare(b.dataset.sortName, a.dataset.sortName),
  };

  options.forEach((option) => {
    option.addEventListener(
      'click',
      () => {
        const compare = comparators[option.dataset.sortValue];
        if (!compare) return;

        cards.sort(compare);
        const fragment = document.createDocumentFragment();
        cards.forEach((card) => fragment.appendChild(card));
        grid.appendChild(fragment);

        // Restart from the first page after re-sorting.
        visible = Math.min(perPage, total);
        applyWindow();

        options.forEach((other) => {
          const active = other === option;
          other.classList.toggle('is-active', active);
          other.setAttribute('aria-checked', active ? 'true' : 'false');
        });

        if (sort.tagName === 'DETAILS') sort.open = false;
      },
      { signal }
    );
  });

  // Keyboard support expected of the `menu` / `menuitemradio` roles: arrow keys
  // (plus Home/End) move between options, Escape closes the menu. Enter/Space
  // already activate the options, since they are real buttons.
  function focusOption(index) {
    options[(index + options.length) % options.length].focus();
  }

  sort.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Escape' && sort.open) {
        sort.open = false;
        if (summary) summary.focus();
        event.preventDefault();
        return;
      }

      // From the closed/focused toggle, ArrowDown opens the menu and steps in.
      if (event.key === 'ArrowDown' && document.activeElement === summary) {
        sort.open = true;
        focusOption(0);
        event.preventDefault();
        return;
      }

      const current = options.indexOf(document.activeElement);
      if (current === -1) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') focusOption(current + 1);
      else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') focusOption(current - 1);
      else if (event.key === 'Home') focusOption(0);
      else if (event.key === 'End') focusOption(options.length - 1);
      else return;

      event.preventDefault();
    },
    { signal }
  );

  // Close the menu when clicking outside of it.
  document.addEventListener(
    'click',
    (event) => {
      if (sort.open && !sort.contains(event.target)) sort.open = false;
    },
    { signal }
  );
}

document.querySelectorAll('[data-designer-overview]').forEach(initDesignerOverview);

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('[data-designer-overview]');
  if (section) initDesignerOverview(section);
});
